import React, { useEffect, useState } from "react";
import Card from "components/card";
import Button from "components/button";
import { useAuthContext } from "hooks/AuthContext";
import { useNavigate } from "react-router-dom";
import { updateCurrentUser, deleteCurrentUserPfp } from "services/users";
import { getAgeGrades } from "services/ageGrades";
import { useToast } from "components/Toast";

const UserProfile: React.FC = () => {
  const { user, setUser } = useAuthContext();
  const toast = useToast();
  const formatDateForInput = (val: any) => {
    if (!val) return "";
    // If already yyyy-mm-dd, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
    // Try to split ISO datetime and return date part
    if (typeof val === "string" && val.includes("T")) return val.split("T")[0];
    try {
      const d = new Date(val);
      if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    } catch (e) {
      // fallthrough
    }
    return "";
  };
  const [ageGrades, setAgeGrades] = useState<any[]>([]);
  const [form, setForm] = useState({
    name: user?.name || "",
    firstname: user?.firstname || "",
    lastname: user?.lastname || "",
    email: user?.email || "",
    phone: user?.phone || "",
    dob: formatDateForInput((user as any)?.dob) || "",
    age_grade_id: (user as any)?.age_grade_id || "",
    village: (user as any)?.village || "",
    pfp: user?.pfp || "",
  });
  const [pfpFile, setPfpFile] = useState<File | null>(null);
  const [pfpPreview, setPfpPreview] = useState<string>("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getAgeGrades();
        const data = res.data?.data ?? res.data ?? [];
        if (mounted) setAgeGrades(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        if (mounted) setAgeGrades([]);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, []);

  // Keep form in sync if `user` updates after mount (formatting dob for date input)
  useEffect(() => {
    if (!user) return;
    setForm({
      name: (user as any)?.name || "",
      firstname: (user as any)?.firstname || "",
      lastname: (user as any)?.lastname || "",
      email: (user as any)?.email || "",
      phone: (user as any)?.phone || "",
      dob: formatDateForInput((user as any)?.dob) || "",
      age_grade_id: (user as any)?.age_grade_id || "",
      village: (user as any)?.village || "",
      pfp: (user as any)?.pfp || "",
    });
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      let payload: any = { ...form };
      delete payload.password;
      delete payload.password_confirmation;
      // Handle profile photo upload
      let updatedUser: any;
      // If not uploading a new file, don't send `pfp` at all (avoid sending existing path string)
      if (!pfpFile) delete payload.pfp;
      if (pfpFile) {
        const formData = new FormData();
        formData.append("pfp", pfpFile);
        // Append other fields (skip null/undefined)
        Object.keys(payload).forEach((key) => {
          if (key === "pfp") return;
          const val = payload[key];
          if (val === undefined || val === null) return;
          formData.append(key, val);
        });
        updatedUser = await updateCurrentUser(formData);
      } else {
        updatedUser = await updateCurrentUser(payload);
      }
      // Set form.pfp to backend path after upload and update global user context
      if (updatedUser && updatedUser.pfp) {
        setForm((f) => ({ ...f, pfp: updatedUser.pfp }));
        setPfpPreview(""); // clear preview after save
        if (setUser) setUser(updatedUser);
      }
      toast.showSuccess?.("Profile updated");
    } catch (err: any) {
      console.error(err);
      // log server response body if available
      if (err?.response?.data)
        console.error("Server validation:", err.response.data);
      // Show validation errors if present
      const serverErrors = err?.response?.data ?? err?.response?.data?.errors;
      if (serverErrors) {
        // If Laravel returns errors array
        const messages = serverErrors.errors
          ? Object.values(serverErrors.errors).flat().join(" \n")
          : serverErrors.message
          ? serverErrors.message
          : typeof serverErrors === "string"
          ? serverErrors
          : JSON.stringify(serverErrors);
        toast.showError?.(messages || "Failed to update profile");
      } else {
        toast.showError?.("Failed to update profile");
      }
    } finally {
      setSaving(false);
    }
  };

  const navigate = useNavigate();
  const { logout } = useAuthContext();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/auth/sign-in", { replace: true });
    } catch (err) {
      console.error(err);
      toast.showError?.("Logout failed");
      // Still redirect to sign-in even if logout fails
      navigate("/auth/sign-in", { replace: true });
    }
  };

  return (
    <Card extra="w-full p-4">
      <h1 className="mb-4 text-2xl font-bold">Profile</h1>
      <form
        onSubmit={handleSubmit}
        className="space-y-4"
        encType="multipart/form-data"
      >
        {/* Profile Photo Upload */}
        <div className="mb-2 flex flex-col items-start">
          <label className="mb-1 font-medium">Profile Photo</label>
          {/* Show preview if a new file is selected, else show backend pfp */}
          {pfpPreview ? (
            <img
              src={pfpPreview}
              alt="Profile preview"
              className="mb-2 h-20 w-20 rounded-full object-cover"
            />
          ) : form.pfp ? (
            <img
              src={
                form.pfp.startsWith("http")
                  ? form.pfp
                  : form.pfp.startsWith("/")
                  ? `${(
                      process.env.REACT_APP_API_URL ||
                      "http://greaternunya.site/api"
                    ).replace(/\/api\/?$/, "")}${form.pfp}`
                  : `${(
                      process.env.REACT_APP_API_URL ||
                      "http://greaternunya.site/api"
                    ).replace(/\/api\/?$/, "")}/storage/${form.pfp}`
              }
              alt="Profile"
              className="mb-2 h-20 w-20 rounded-full object-cover"
            />
          ) : null}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setPfpFile(e.target.files[0]);
                setPfpPreview(URL.createObjectURL(e.target.files[0]));
              }
            }}
          />
          {/* Delete photo button */}
          {form.pfp && (
            <button
              type="button"
              className="mt-2 text-sm text-red-600"
              onClick={async () => {
                try {
                  setSaving(true);
                  await deleteCurrentUserPfp();
                  setForm((f) => ({ ...f, pfp: "" }));
                  setPfpPreview("");
                  if (setUser) setUser({ ...(user as any), pfp: null });
                  toast.showSuccess?.("Profile photo removed");
                } catch (err) {
                  console.error(err);
                  toast.showError?.("Failed to remove profile photo");
                } finally {
                  setSaving(false);
                }
              }}
            >
              Remove photo
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="firstname"
            value={form.firstname}
            onChange={handleChange}
            className="input"
            placeholder="First name"
          />
          <input
            name="lastname"
            value={form.lastname}
            onChange={handleChange}
            className="input"
            placeholder="Last name"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="email"
            value={form.email}
            onChange={handleChange}
            className="input"
            placeholder="Email"
          />
          <input
            name="phone"
            value={form.phone}
            onChange={handleChange}
            className="input"
            placeholder="Phone"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <input
            name="dob"
            value={form.dob}
            onChange={handleChange}
            className="input"
            placeholder="Date of birth"
            type="date"
          />
          <select
            name="village"
            value={form.village}
            onChange={handleSelect}
            className="input"
          >
            <option value="">Select village</option>
            <option value="Ehihie">Ehihie</option>
            <option value="Eziama">Eziama</option>
            <option value="Umuezumoha">Umuezumoha</option>
            <option value="Avuvu">Avuvu</option>
          </select>
        </div>
        <div>
          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
            <Button
              type="button"
              onClick={() => navigate("/user/change-password")}
            >
              Change Password
            </Button>
            <Button type="button" color="red" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </form>
    </Card>
  );
};

export default UserProfile;
